import React, { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Divider, Form, Input, Modal, Row, Select, Table, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PaperClipOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import OfferItemModal from './OfferItemModal';

const { Option } = Select;
const { Title } = Typography;
const { TextArea } = Input;

const getInitialOfferType = (modalType, editingOfferType) => {
  if (modalType === 'editOffer') return editingOfferType;
  if (modalType === 'createOffer') return 'Offer';
  if (modalType === 'createOrder') return 'Order';
  return undefined;
};

export default function OfferOrderModal({
  modalType,
  isModalVisible,
  handleCancel,
  selectedLead,
  leads,
  products,
  categories,
  services,
  logisticsItems,
  suppliers,
  supplierProducts,
  editingOffer,
  isRepeat = false,
  offerItems,
  setOfferItems,
  handleRemoveItem,
  calculateTotal,
  calculateInternalTotal,
  onCreateCounterparty,
  onSave,
  canDelete = true,
}) {
  const [counterpartyForm] = Form.useForm();
  const isEdit = modalType === 'editOffer';
  const [selectedType, setSelectedType] = useState(getInitialOfferType(modalType, editingOffer?.type));
  const [descriptionToCustomer, setDescriptionToCustomer] = useState(editingOffer?.descriptionToCustomer || '');
  const [deliveryDate, setDeliveryDate] = useState(editingOffer?.deliveryDate ? dayjs(editingOffer.deliveryDate) : null);
  const [paymentTerms, setPaymentTerms] = useState(editingOffer?.paymentTerms || '');
  const [selectedClientId, setSelectedClientId] = useState(editingOffer?.clientId || selectedLead?.id);
  const [isCounterpartyModalOpen, setIsCounterpartyModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const selectedClientName = leads.find((lead) => lead.id === selectedClientId)?.name || editingOffer?.client || selectedLead?.name || '';
  const hasInquirySourceItems = offerItems.some((item) => Boolean(item.inquiryItemId));

  useEffect(() => {
    setSelectedClientId(editingOffer?.clientId || selectedLead?.id);
    setSelectedType(getInitialOfferType(modalType, editingOffer?.type));
    setDescriptionToCustomer(editingOffer?.descriptionToCustomer || '');
    setDeliveryDate(editingOffer?.deliveryDate ? dayjs(editingOffer.deliveryDate) : null);
    setPaymentTerms(editingOffer?.paymentTerms || '');
  }, [
    editingOffer?.clientId,
    editingOffer?.deliveryDate,
    editingOffer?.descriptionToCustomer,
    editingOffer?.paymentTerms,
    editingOffer?.type,
    modalType,
    selectedLead?.id,
    isModalVisible,
  ]);

  const handleSubmit = () => {
    if (!selectedType) {
      message.warning('Please select type');
      return;
    }
    if (!selectedClientId) {
      message.warning('Please select client');
      return;
    }

    const savedOffer = onSave?.({
      type: selectedType,
      clientId: selectedClientId,
      descriptionToCustomer,
      deliveryDate: deliveryDate ? deliveryDate.format('YYYY-MM-DD') : '',
      paymentTerms,
    });
    handleCancel();
    message.success(isEdit
      ? `${editingOffer?.type || 'Offer/Order'} updated`
      : `${selectedType} created${savedOffer?.id ? `: ${savedOffer.id}` : ''}`);
  };

  const handleSaveItem = (itemData) => {
    setOfferItems?.((prev) => {
      const exists = prev.some((i) => i.id === itemData.id);
      return exists ? prev.map((i) => (i.id === itemData.id ? itemData : i)) : [...prev, itemData];
    });
    setItemModalOpen(false);
    setEditingItem(null);
  };

  const handleCreateCounterpartySubmit = async () => {
    try {
      const values = await counterpartyForm.validateFields();
      const createdLead = onCreateCounterparty?.(values);
      if (createdLead) {
        setSelectedClientId(createdLead.id);
        setIsCounterpartyModalOpen(false);
        counterpartyForm.resetFields();
      }
    } catch (error) {
      // Validation is shown in the form.
    }
  };

  return (
    <>
      <Modal
        title={isEdit ? `Edit ${editingOffer?.type || 'Offer/Order'}` : isRepeat ? `Repeat ${editingOffer?.type || 'Offer/Order'}` : hasInquirySourceItems ? `Convert to ${selectedType || 'Offer'}` : (selectedType ? `Create ${selectedType}` : 'Create Offer/Order')}
        open={isModalVisible}
        onCancel={handleCancel}
        width={1000}
        footer={[
          <Button key="cancel" onClick={handleCancel}>Cancel</Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit}
            disabled={offerItems.length === 0}
          >
            {isEdit ? 'Update' : isRepeat ? 'Create copy' : 'Create'}
          </Button>,
        ]}
      >
        <Form layout="vertical">
        <Form.Item label="Type" required>
          <Select placeholder="Select type" value={selectedType} onChange={setSelectedType}>
            <Option value="Offer">Offer</Option>
            <Option value="Order">Order</Option>
          </Select>
        </Form.Item>
        {!isEdit && (
          <Row gutter={12}>
            <Col flex="auto">
              <Form.Item label={isEdit ? 'Client Name' : 'Select Client'} required>
                {isEdit ? (
                  <Input value={selectedClientName} readOnly />
                ) : (
                  <Select
                    showSearch
                    placeholder="Search by name, phone, email, or CVR"
                    value={selectedClientId}
                    onChange={setSelectedClientId}
                    optionFilterProp="children"
                  >
                    {leads.map(lead => (
                      <Option key={lead.key} value={lead.id}>
                        {lead.name} - {lead.cvr}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
            {!isEdit && (
              <Col>
                <Form.Item label=" ">
                  <Button
                    onClick={() => {
                      counterpartyForm.resetFields();
                      counterpartyForm.setFieldsValue({ status: 'Lead/Prospect' });
                      setIsCounterpartyModalOpen(true);
                    }}
                  >
                    Add Prospect/Client
                  </Button>
                </Form.Item>
              </Col>
            )}
          </Row>
        )}

        {!isEdit && !hasInquirySourceItems && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => { setEditingItem(null); setItemModalOpen(true); }}
              >
                Add Item
              </Button>
            </div>
          </>
        )}

        {!isEdit && offerItems.length > 0 && (
          <>
            <Divider>Items</Divider>
            <Table
              className="offer-items-table"
              dataSource={offerItems}
              columns={[
                {
                  title: 'Item',
                  dataIndex: 'productName',
                  key: 'productName',
                  width: 220,
                  render: (value, record) => (
                    <div>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
                      {record.article && <div style={{ color: '#8c8c8c', fontSize: 12 }}>Article: {record.article}</div>}
                    </div>
                  ),
                },
                {
                  title: 'Supplier',
                  dataIndex: 'supplierName',
                  key: 'supplierName',
                  width: 170,
                  ellipsis: true,
                  render: (value) => value || 'No supplier',
                },
                {
                  title: 'Qty',
                  dataIndex: 'quantity',
                  key: 'quantity',
                  width: 70,
                  align: 'right',
                },
                {
                  title: 'Base price',
                  dataIndex: 'baseInternalPrice',
                  key: 'baseInternalPrice',
                  width: 90,
                  align: 'right',
                  render: (value) => Number(value || 0).toLocaleString('da-DK'),
                },
                {
                  title: 'Production',
                  dataIndex: 'productionCost',
                  key: 'productionCost',
                  width: 90,
                  align: 'right',
                  render: (value) => Number(value || 0).toLocaleString('da-DK'),
                },
                {
                  title: 'Delivery',
                  dataIndex: 'deliveryCost',
                  key: 'deliveryCost',
                  width: 85,
                  align: 'right',
                  render: (value) => Number(value || 0).toLocaleString('da-DK'),
                },
                {
                  title: 'Margin %',
                  dataIndex: 'margin',
                  key: 'margin',
                  width: 90,
                  align: 'right',
                  render: (margin) => `${Number(margin || 0).toLocaleString('da-DK')}%`,
                },
                {
                  title: 'Discount %',
                  dataIndex: 'discount',
                  key: 'discount',
                  width: 95,
                  align: 'right',
                  render: (discount) => `${Number(discount || 0).toLocaleString('da-DK')}%`,
                },
                {
                  title: 'Customer price',
                  dataIndex: 'customerPrice',
                  key: 'customerPrice',
                  width: 125,
                  align: 'right',
                  render: (price) => Number(price || 0).toLocaleString('da-DK'),
                },
                {
                  title: 'Total cost',
                  dataIndex: 'internalPrice',
                  key: 'internalPrice',
                  width: 90,
                  align: 'right',
                  render: (price) => Number(price || 0).toLocaleString('da-DK'),
                },
                {
                  title: 'Cost total',
                  dataIndex: 'internalLineTotal',
                  key: 'internalLineTotal',
                  width: 105,
                  align: 'right',
                  render: (total, record) => <strong>{Number(total ?? ((record.internalPrice || 0) * (record.quantity || 0))).toLocaleString('da-DK')}</strong>,
                },
                {
                  title: 'Total',
                  dataIndex: 'lineTotal',
                  key: 'lineTotal',
                  width: 105,
                  align: 'right',
                  render: (total) => <strong>{total.toLocaleString('da-DK')}</strong>,
                },
                {
                  title: 'Notes',
                  dataIndex: 'notes',
                  key: 'notes',
                  width: 170,
                  ellipsis: true,
                },
                {
                  title: 'Files',
                  key: 'files',
                  width: 90,
                  render: (_, record) => {
                    const fileCount = record.files?.length || 0;
                    return fileCount ? (
                      <span><PaperClipOutlined /> {fileCount}</span>
                    ) : <span style={{ color: '#bfbfbf' }}>—</span>;
                  },
                },
                ...(hasInquirySourceItems ? [] : [{
                  title: 'Action',
                  key: 'action',
                  width: 90,
                  render: (_, record) => (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        style={{ padding: 0, height: 'auto' }}
                        onClick={() => { setEditingItem(record); setItemModalOpen(true); }}
                      >
                        Edit
                      </Button>
                      {canDelete && (
                        <Button
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ padding: 0, height: 'auto' }}
                          onClick={() => handleRemoveItem(record.id)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  ),
                }]),
              ]}
              pagination={false}
              bordered
              size="small"
              rowKey="id"
              tableLayout="fixed"
              scroll={{ x: 1600 }}
            />
          </>
        )}

        <Divider>Customer Details</Divider>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Description to customer">
              <TextArea
                rows={3}
                value={descriptionToCustomer}
                onChange={(event) => setDescriptionToCustomer(event.target.value)}
                placeholder="Customer-facing offer notes"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Delivery date">
              <DatePicker
                value={deliveryDate}
                onChange={setDeliveryDate}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Payment terms">
              <Input
                value={paymentTerms}
                onChange={(event) => setPaymentTerms(event.target.value)}
                placeholder="Net 14 days"
              />
            </Form.Item>
          </Col>
        </Row>

        <div style={{
          textAlign: 'right',
          padding: '16px',
          backgroundColor: '#f0f0f0',
          borderRadius: '4px',
          marginTop: '16px',
        }}>
          <Title level={4} style={{ margin: 0 }}>
            Internal total: {calculateInternalTotal().toLocaleString('da-DK')} DKK
            <span style={{ marginLeft: 24 }}>
              Customer total: {calculateTotal().toLocaleString('da-DK')} DKK
            </span>
          </Title>
        </div>
        </Form>
      </Modal>

      <Modal
        title="Add New Lead/Client"
        open={isCounterpartyModalOpen}
        onCancel={() => {
          setIsCounterpartyModalOpen(false);
          counterpartyForm.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsCounterpartyModalOpen(false);
              counterpartyForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleCreateCounterpartySubmit}>
            Create
          </Button>,
        ]}
        width={800}
        destroyOnClose
      >
        <Form form={counterpartyForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Client Name" name="name" rules={[{ required: true, message: 'Please enter client name' }]}>
                <Input placeholder="Client name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="CVR Number" name="cvr" rules={[{ required: true, message: 'Please enter CVR number' }]}>
                <Input placeholder="CVR number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Email" name="email" rules={[{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Please enter a valid email' }]}>
                <Input placeholder="email@example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Phone" name="phone" rules={[{ required: true, message: 'Please enter phone' }]}>
                <Input placeholder="+45 12 34 56 78" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Status" name="status" rules={[{ required: true, message: 'Please select status' }]}>
                <Select>
                  <Option value="Lead/Prospect">Lead/Prospect</Option>
                  <Option value="Customer">Customer</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Address" name="address">
                <Input placeholder="Full address" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <OfferItemModal
        open={itemModalOpen}
        item={editingItem}
        categories={categories}
        products={products}
        services={services}
        logisticsItems={logisticsItems}
        suppliers={suppliers}
        supplierProducts={supplierProducts}
        otherItems={offerItems}
        onCancel={() => { setItemModalOpen(false); setEditingItem(null); }}
        onSave={handleSaveItem}
      />
    </>
  );
}
